import {
  HttpInterceptorFn,
  HttpRequest,
  HttpHandlerFn,
  HttpErrorResponse,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from '../services/auth.service';
import { ErrorCode } from '../../shared/enums/error-code.enum';

export const errorInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
) => {
  const router = inject(Router);
  const authService = inject(AuthService);
  const toastr = inject(ToastrService);
  const translate = inject(TranslateService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      let errorMessage = translate.instant('toastr.messages.unexpectedError');
      let errorCode: ErrorCode = ErrorCode.UNKNOWN_ERROR;

      if (error.error instanceof ErrorEvent) {
        // Client-side error
        errorMessage = error.error.message;
        errorCode = ErrorCode.CLIENT_ERROR;
        toastr.error(errorMessage, translate.instant('toastr.titles.error'), {
          timeOut: 4000,
          positionClass: 'toast-top-right'
        });
      } else {
        // Server-side error
        errorCode = error.error?.code || ErrorCode.SERVER_ERROR;

        switch (error.status) {
          case 0:
            errorMessage = translate.instant('toastr.messages.connectionFailed');
            errorCode = ErrorCode.NETWORK_ERROR;
            toastr.error(errorMessage, translate.instant('toastr.titles.connectionError'), {
              timeOut: 5000,
              positionClass: 'toast-top-right'
            });
            break;

          case 400:
            errorMessage = error.error?.message || translate.instant('toastr.messages.badRequest');
            errorCode = ErrorCode.BAD_REQUEST;
            toastr.warning(errorMessage, translate.instant('toastr.titles.invalidRequest'), {
              timeOut: 4000,
              positionClass: 'toast-top-right'
            });
            break;

          case 401:
            errorMessage = error.error?.message || translate.instant('toastr.messages.sessionExpired');
            errorCode = ErrorCode.UNAUTHORIZED;
            // Only logout if not already on login page
            if (!req.url.includes('/Auth/login')) {
              toastr.error(errorMessage, translate.instant('toastr.titles.sessionExpired'), {
                timeOut: 4000,
                positionClass: 'toast-top-right'
              });
              authService.logout();
            } else {
              // Login failed - show specific message
              toastr.error(error.error?.message || translate.instant('toastr.messages.invalidCredentials'), translate.instant('toastr.titles.loginFailed'), {
                timeOut: 4000,
                positionClass: 'toast-top-right'
              });
            }
            break;

          case 403:
            errorMessage = translate.instant('toastr.messages.accessDenied');
            errorCode = ErrorCode.FORBIDDEN;
            toastr.error(errorMessage, translate.instant('toastr.titles.accessDenied'), {
              timeOut: 4000,
              positionClass: 'toast-top-right'
            });
            router.navigate(['/unauthorized']);
            break;

          case 404:
            errorMessage = error.error?.message || translate.instant('toastr.messages.notFound');
            errorCode = ErrorCode.NOT_FOUND;
            toastr.warning(errorMessage, translate.instant('toastr.titles.notFound'), {
              timeOut: 4000,
              positionClass: 'toast-top-right'
            });
            break;

          case 409:
            errorMessage = error.error?.message || translate.instant('toastr.messages.conflict');
            errorCode = ErrorCode.CONFLICT;
            toastr.warning(errorMessage, translate.instant('toastr.titles.conflict'), {
              timeOut: 4000,
              positionClass: 'toast-top-right'
            });
            break;

          case 422:
            errorMessage = error.error?.message || translate.instant('toastr.messages.validationError');
            errorCode = ErrorCode.VALIDATION_ERROR;
            toastr.warning(errorMessage, translate.instant('toastr.titles.validationError'), {
              timeOut: 4000,
              positionClass: 'toast-top-right'
            });
            break;

          case 429:
            errorMessage = translate.instant('toastr.messages.rateLimited');
            errorCode = ErrorCode.TOO_MANY_REQUESTS;
            toastr.warning(errorMessage, translate.instant('toastr.titles.rateLimited'), {
              timeOut: 5000,
              positionClass: 'toast-top-right'
            });
            break;

          case 500:
            errorMessage = translate.instant('toastr.messages.serverError');
            errorCode = ErrorCode.SERVER_ERROR;
            toastr.error(errorMessage, translate.instant('toastr.titles.serverError'), {
              timeOut: 5000,
              positionClass: 'toast-top-right'
            });
            break;

          case 502:
          case 503:
          case 504:
            errorMessage = translate.instant('toastr.messages.serviceUnavailable');
            errorCode = ErrorCode.SERVICE_UNAVAILABLE;
            toastr.error(errorMessage, translate.instant('toastr.titles.serviceUnavailable'), {
              timeOut: 5000,
              positionClass: 'toast-top-right'
            });
            break;

          default:
            errorMessage = error.error?.message || `Error: ${error.status} - ${error.statusText}`;
            toastr.error(errorMessage, translate.instant('toastr.titles.error'), {
              timeOut: 4000,
              positionClass: 'toast-top-right'
            });
        }
      }

      return throwError(() => ({
        message: errorMessage,
        code: errorCode,
        status: error.status,
        originalError: error,
      }));
    })
  );
};
